import CircleIcon from '@mui/icons-material/Circle';
import SearchIcon from '@mui/icons-material/Search';
import {
  ClickAwayListener,
  FormControl,
  InputAdornment,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import TextField from '@mui/material/TextField';
import React, { KeyboardEvent, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { getCurrentCompanies } from 'src/modules/companies/slices';

type CompanySwitcherProps = {
  selectedCompanyId: string;
  onSwitch: (companyId: string) => void;
  variant?: 'desktop' | 'mobile';
};

const getAvailabilityStatus = (availabilityStatus: string) => {
  switch (availabilityStatus) {
    case 'available':
      return (
        <CircleIcon sx={{ scale: '0.7', marginTop: '2px' }} fontSize="small" htmlColor={'green'} />
      );
    case 'away':
      return (
        <CircleIcon sx={{ scale: '0.7', marginTop: '2px' }} fontSize="small" htmlColor={'red'} />
      );
    case 'no_active_plan':
      return (
        <CircleIcon sx={{ scale: '0.7', marginTop: '2px' }} fontSize="small" htmlColor={'grey'} />
      );
    default:
      return (
        <CircleIcon sx={{ scale: '0.7', marginTop: '2px' }} fontSize="small" htmlColor={'grey'} />
      );
  }
};

export const CompanySwitcher: React.FC<CompanySwitcherProps> = ({
  selectedCompanyId,
  onSwitch,
  variant = 'desktop',
}) => {
  const [searchCompanyTerm, setSearchCompanyTerm] = useState<string>('');
  const [open, setOpen] = useState(false);
  const companySearchRef = useRef<HTMLDivElement | HTMLTextAreaElement>(null);
  const companies = useSelector(getCurrentCompanies);
  const theme = useTheme();
  const isMobileViewport = useMediaQuery(theme.breakpoints.down('md'));
  const isMobileVariant = variant === 'mobile';
  const useCompactSearchField = isMobileVariant || isMobileViewport;

  useEffect(() => {
    if (open) {
      setSearchCompanyTerm('');
    }
  }, [open]);

  const filteredCompanies = companies
    ?.filter(
      c =>
        c.name.toLowerCase().includes(searchCompanyTerm.toLowerCase()) ||
        c.id === selectedCompanyId,
    )
    ?.sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));

  return (
    <ClickAwayListener
      onClickAway={() => {
        setOpen(false);
      }}
    >
      <FormControl
        style={{
          cursor: companies.length > 1 ? 'pointer' : 'default',
          width: isMobileVariant ? 'auto' : '200px',
          marginRight: isMobileVariant ? '8px' : '20px',
        }}
        variant="standard"
        {...(isMobileVariant && {
          sx: {
            minWidth: 60,
            maxWidth: 120,
            flex: 1,
            display: 'flex',
            justifyContent: 'center',
          },
        })}
      >
        <Select
          sx={{
            position: 'relative',
            '& #bhi-select-company': {
              cursor: companies.length > 1 ? 'pointer' : 'default',
            },
            '& .MuiInputBase-input': {
              paddingBottom: 0,
            },
            '& .MuiSelect-select': {
              paddingLeft: isMobileVariant ? '20px' : '25px',
              ...(isMobileVariant && { fontSize: '0.8rem' }),
            },
            '& .MuiSelect-select .MuiSvgIcon-root': {
              position: 'absolute',
              left: '2px',
              top: 'calc(50% - .55em)',
            },
            '& .MuiSelect-icon': {
              display: companies && companies.length === 1 ? 'none' : 'initial',
              ...(isMobileVariant && { color: 'black' }),
            },
            '& .MuiFormControl-root': {
              cursor: companies.length > 1 ? 'pointer' : 'default',
            },
            ...(isMobileVariant && {
              color: 'black',
              fontSize: '0.8rem',
              '& .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '&:hover .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
              '&.Mui-focused .MuiOutlinedInput-notchedOutline': { borderColor: 'transparent' },
            }),
          }}
          onClick={() => {
            if (companies && companies.length === 1) {
              return;
            }
            setOpen(!open);
            window.setTimeout(() => {
              if (companySearchRef && companySearchRef.current) {
                companySearchRef.current.focus();
              }
            }, 50);
          }}
          id="bhi-select-company"
          value={selectedCompanyId}
          open={open}
          MenuProps={{
            disablePortal: false,
            PaperProps: {
              sx: {
                cursor: companies.length > 1 ? 'pointer' : 'default',
                '& .MuiMenu-list': {
                  paddingTop: 0,
                },
                '& .MuiMenuItem-root:hover': {
                  backgroundColor: 'background.paper',
                },
                '& .MuiMenuItem-root:first-child': {
                  backgroundColor: 'white !important',
                },
              },
            },
          }}
          label="Company"
        >
          {companies && companies.length > 1 && (
            <MenuItem
              sx={{
                backgroundColor: 'background.paper',
                position: 'sticky',
                top: '0px',
                zIndex: '999999',
                paddingRight: 0,
                display: 'block',
                minHeight: '48px',
              }}
              onClick={event => {
                event.preventDefault();
                event.stopPropagation();
              }}
            >
              <TextField
                sx={{
                  '& .MuiOutlinedInput-input': {
                    height: '35px',
                    width: '100%',
                    paddingTop: 0,
                    paddingBottom: 0,
                  },
                  marginLeft: '-8px',
                  width: '100%',
                  display: 'block',
                  visibility: 'visible',
                  ...(useCompactSearchField && {
                    minHeight: '40px',
                    '& .MuiOutlinedInput-root': {
                      minHeight: '40px',
                    },
                  }),
                }}
                id="nested-select-filter"
                placeholder="Search..."
                variant="outlined"
                fullWidth
                inputRef={companySearchRef}
                value={searchCompanyTerm}
                autoComplete="off"
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start" sx={{ marginLeft: '-10px' }}>
                      <SearchIcon fontSize="small" />
                    </InputAdornment>
                  ),
                }}
                InputLabelProps={{ shrink: false }}
                onKeyDown={(e: KeyboardEvent<HTMLDivElement>) => {
                  e.stopPropagation();
                  if (e.key === 'ArrowDown' && companySearchRef && companySearchRef.current) {
                    companySearchRef.current.blur();
                    const companiesList = document.getElementById('bhi-select-company');
                    if (companiesList) {
                      (document.querySelectorAll('.companyName')[0] as HTMLElement).focus();
                    }
                  }
                }}
                onChange={e => {
                  e.preventDefault();
                  setSearchCompanyTerm(e.target.value);
                }}
              />
            </MenuItem>
          )}
          {filteredCompanies.map(({ id, name, availabilityStatus }) => {
            return (
              <MenuItem
                sx={{
                  display:
                    id === selectedCompanyId && !name.toLowerCase().includes(searchCompanyTerm)
                      ? 'none'
                      : 'flex',
                  height:
                    id === selectedCompanyId && !name.toLowerCase().includes(searchCompanyTerm)
                      ? '0'
                      : 'auto',
                  alignItems: 'center',
                }}
                className="companyName"
                onClick={() => {
                  setOpen(false);
                  onSwitch(id);
                }}
                value={id}
                key={id}
              >
                {getAvailabilityStatus(availabilityStatus)} {name}
              </MenuItem>
            );
          })}
        </Select>
      </FormControl>
    </ClickAwayListener>
  );
};
